// app/composables/season/useSeasonCascade.ts

import type { Season } from "~/src/season-cluster/domain/models/01.Season";
import type { SimplifiedSeasonCluster } from "~/src/season-cluster/domain/models/00.SeasonCluster";

/**
 * Composable to manage cascading season filter selections
 * Handles automatic selection of last seasonCluster and last season when region/cluster changes
 *
 * Note: Assumes seasonClusters are sorted by finish date (DESC) - verified in competitionStore
 */
export const useSeasonCascade = () => {
  /**
   * Finds the last (most recent) season cluster available for a given region
   * @param seasonClusters - Array of available season clusters (sorted by start date ASC from API)
   * @param regionId - The region ID to filter by
   * @param allSeasons - All available seasons to check for matches
   * @param filterSeasonsFn - Function to filter seasons for the region
   * @returns The last available season cluster ID, or undefined if none found
   */
  const findLastSeasonClusterForRegion = <T extends { season?: Season } | Season>(
    seasonClusters: SimplifiedSeasonCluster[],
    regionId: number,
    allSeasons: T[],
    filterSeasonsFn: (seasons: T[], seasonClusterId: number, regionId: number) => T[]
  ): number | undefined => {
    if (!seasonClusters?.length || !allSeasons?.length) {
      return undefined;
    }

    // seasonClusters are sorted by start date (ASC) from API
    // We need to iterate BACKWARDS to find the LAST cluster that has seasons in this region
    for (let i = seasonClusters.length - 1; i >= 0; i--) {
      const cluster = seasonClusters[i];
      const matchingSeasons = filterSeasonsFn(allSeasons, cluster.id, regionId);

      if (matchingSeasons.length > 0) {
        return cluster.id; // Return the last (most recent) cluster with seasons
      }
    }

    return undefined;
  };

  /**
   * Finds the last (most recent) season available within a season cluster
   * @param seasons - Array of seasons (will be sorted by start date)
   * @returns The last available season, or undefined if none found
   */
  const findLastSeasonInCluster = <T>(seasons: T[]): T | undefined => {
    if (!seasons?.length) {
      return undefined;
    }

    // Return the last season in the array (assumed to be sorted by start date)
    return seasons[seasons.length - 1];
  };

  return {
    findLastSeasonClusterForRegion,
    findLastSeasonInCluster,
  };
};
